import { Model } from 'app/model/backendmodel';
import { ColumnMappingPair } from './../../pipeline/mappingpair';
import { GlobalVariables } from 'app/services/globals';
import { isDeltaCloneCopy } from 'app/authoring/copy-job/wizard/copy-job-delta.util';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ActivityAsyncAdapter } from './activity.adapter';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import { Dataset } from 'app/model/dataset/dataset';
import {
    CopyActivity,
    supportTypeConversion,
    supportDataConsistency,
    supportSkipMissingFile,
    supportSkipForbiddenFile,
    supportSkipFilesWithInvalidNames
} from 'app/model/pipeline/copy.activity';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { convertParametersToModel, fillParameterValuesForResource } from 'app/shared/utils/parameter-converter.util';
import { FaultTolerenceSettings } from 'app/model/pipeline/fault-tolerence-setting';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { StagingSettings } from 'app/model/pipeline/staging-settings';
import { GenericCopySinkAdapter } from 'app/model/adapters/activities/copysink/generic.copysink.adapter';
import { CopySink } from 'app/model/pipeline/copysink/copysink';
import { GenericCopySourceAdapter } from 'app/model/adapters/activities/copysource/generic.copysource.adapter';
import { CopySource } from 'app/model/pipeline/copysource/copysource';
import { AzureTableSink } from '../../pipeline/copysink/azure-table.copysink';
import { ManagedIntegrationRuntime } from 'app/model/integrationRuntime/managed.integrationRuntime';
import { IntegrationRuntimeStatusService } from '../../../integrationruntime/ir-service/integrationruntime-status.service';
import { SelfHostedIntegrationRuntime } from 'app/model/integrationRuntime/selfhosted.integrationRuntime';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';
import { type DatasetRegistryService } from '../../../registries/dataset/dataset-registry.service';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';
import { Column } from 'app/model/dataset/column';
import { preserveRulesValues } from 'app/shared/constants/copy-performance-setting.constant';
import { IParquetPorperties, ModelUtilties, Owned, ParquetDataset, TabularCopySource } from 'shell-models-lib';
import { filter, take } from 'rxjs/operators';
import { TypeConversionSettings } from 'app/model/pipeline/type-conversion-settings';
import { BinaryDataset } from 'app/model/dataset/binary.dataset';
import { linkedServiceReferenceToResource } from 'app/shared/utils/linkedservice-parameter.util';
import { isAdaptivePerformanceTuningSupported, supportPreserveRules } from 'app/shared/utils/activity-settings.util';
import { LoggingSettings } from 'app/model/pipeline/logging-settings';
import { LoggingLocation } from 'app/model/pipeline/logging-location';
import { generateUUID } from 'app/shared/utils/uuid.util';
import { type LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { isMap, merge, noop } from 'lodash';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { resultIsTrue } from 'app/registries/utls/util';
import { ColumnFlattenSettings } from 'app/model/pipeline/column-flatten-settings';
import { supportSqlEditSinkSchemaPhysicalType, toMappingItem } from 'app/shared/utils/column-mapping.util';
import {
    ActivityConnectionContext,
    ICopyActivityContext,
    IDatabaseConnectionContext
} from 'app/interfaces/copilot.pipeline.action.types';
import { type ModelService } from 'app/model/services/model.service';
import { tableOptionValues } from 'app/registries/consts/metadata-consts';
import {
    findLinkedServiceFromContext,
    setDbFromLs,
    setDestinationDataset,
    setSourceDataset
} from 'app/shared/utils/update-pipeline-skill.util';
import { createDatasetWithLinkedService } from 'app/authoring/component/activities/activity-inline-dataset/utils';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { IFrameService } from 'app/services/iframe.service';
import { supportAutoTableCreation, supportEditPhysicalType } from 'app/shared/utils/dataset-utils';
import { getLinkedServiceTypeFromDatasetMetadataAndConnectionKind } from 'app/shared/utils/linkedservice-type.util';

export const copyActivityAdapts = {
    name: '*',
    description: '*',
    type: '*',
    policy: '*',
    inputs: '*',
    outputs: '*',
    dependsOn: '*',
    typeProperties: {
        source: '*',
        sink: '*',
        translator: '*',
        enableStaging: '*',
        stagingSettings: {
            linkedServiceName: '*',
            path: '*',
            enableCompression: '*'
        },
        parallelCopies: '*',
        adaptivePerformanceTuning: __IS_FABRIC_DPE__ ? '*' : undefined,
        cloudDataMovementUnits: '*',
        dataIntegrationUnits: '*',
        preserveRules: '*',
        preserve: '*',
        enableSkipIncompatibleRow: '*',
        redirectIncompatibleRowSettings: {
            linkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer,
            path: '*'
        },
        logStorageSettings: {
            linkedServiceName: '*',
            path: '*',
            logLevel: '*',
            enableReliableLogging: '*'
        },
        logSettings: {
            enableCopyActivityLog: '*',
            enableOdbcDiagnosticLog: '*',
            copyActivityLogSettings: {
                logLevel: '*',
                enableReliableLogging: '*'
            },
            logLocationSettings: {
                linkedServiceName: '*',
                path: '*'
            }
        },
        skipErrorFile: {
            fileMissing: '*',
            fileForbidden: '*',
            dataInconsistency: '*',
            invalidFileName: '*'
        },
        validateDataConsistency: '*'
    },
    userProperties: '*'
};

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(CopyActivity),
    asynchronous: true,
    adapts: copyActivityAdapts
})
export class CopyActivityAsyncAdapter extends ActivityAsyncAdapter<
    CopyActivity,
    Model.CopyActivity,
    ICopyActivityContext
> {
    public model(): IModel {
        return Metamodel.modelFor(CopyActivity);
    }

    public override async postToModel(copyActivity: CopyActivity): Promise<void> {
        await super.postToModel(copyActivity);

        /**
         * In `_toModel`, we can't distinguish mapping v1 or mapping v2 when using expression, so we assign the
         * expression value to both `mapping` and `mappingV2`. But it will cause error in validating expression,
         * so we need to correct it here.
         *
         * We should do this only both `sourceDataset` and `sinkDataset` exist.
         */
        if (copyActivity?.sourceDataset && copyActivity?.sinkDataset) {
            if (!copyActivity.sourceDataset.isV2 && !copyActivity.sinkDataset.isV2) {
                CopyActivity.MAPPING_V2.setTemplateValue(copyActivity, undefined);
            } else {
                CopyActivity.MAPPING.setTemplateValue(copyActivity, undefined);
            }
            if (__IS_FABRIC_DPE__) {
                const datasetRegistry = await getServiceInstance<DatasetRegistryService>(
                    this.context.contextData,
                    'DatasetRegistryService'
                );
                const iframeService = await getServiceInstance<IFrameService>(
                    this.context.contextData,
                    'IFrameService'
                );
                copyActivity.sourceDataset.customLinkedServiceType =
                    this._getLSTpeFromDatasetMetadataAndConnectionKind(datasetRegistry, iframeService, copyActivity.sourceDataset);
                copyActivity.sinkDataset.customLinkedServiceType =
                    this._getLSTpeFromDatasetMetadataAndConnectionKind(datasetRegistry, iframeService, copyActivity.sinkDataset);
            }
        }

        if (this.context.adapterConfig.inlineDataset) {
            if (copyActivity.sourceDataset) {
                const sourceDatasetModel = Metamodel.modelOf(copyActivity.sourceDataset);
                if (sourceDatasetModel) {
                    const sourceDatasetAdapter = this.context.asyncAdapterFor(sourceDatasetModel);
                    if (sourceDatasetAdapter) {
                        await sourceDatasetAdapter.postToModel(copyActivity.sourceDataset);
                    }
                }
            }

            if (copyActivity.sinkDataset) {
                const sinkDatasetModel = Metamodel.modelOf(copyActivity.sinkDataset);
                if (sinkDatasetModel) {
                    const sinkDatasetAdapter = this.context.asyncAdapterFor(sinkDatasetModel);
                    if (sinkDatasetAdapter) {
                        await sinkDatasetAdapter.postToModel(copyActivity.sinkDataset);
                    }
                }
            }
        }
    }

    public override async toFabricCopilotContext(activity: CopyActivity): Promise<ICopyActivityContext> {
        if (activity) {
            const activityContext: ICopyActivityContext = await super.toFabricCopilotContext(activity);
            if (activity.sourceDataset) {
                activityContext.SourceConnection = await this.getDatasetCopilotContext(activity.sourceDataset);
                if (
                    activity.source instanceof TabularCopySource &&
                    activity.source.mode === 'query' &&
                    activityContext.SourceConnection
                ) {
                    activityContext.SourceConnection = {
                        ...activityContext.SourceConnection,
                        Query: ValueAdapter.toResource(activity.source, TabularCopySource.READER_QUERY, true)
                    };
                }
            }
            if (activity.sinkDataset) {
                activityContext.TargetConnection = await this.getDatasetCopilotContext(activity.sinkDataset);
            }
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: ICopyActivityContext,
        model?: CopyActivity
    ): Promise<CopyActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            const datasetRegistry = await getServiceInstance<DatasetRegistryService>(
                this.context.contextData,
                'DatasetRegistryService'
            );
            const linkedServiceRegistry = await getServiceInstance<LinkedServiceRegistryService>(
                this.context.contextData,
                'LinkedServiceRegistryService'
            );
            await Promise.all(
                [
                    { location: 'source', connection: context.SourceConnection },
                    { location: 'destination', connection: context.TargetConnection }
                ]
                    .filter((item) => Boolean(item.connection))
                    .map((item) => ({
                        connection: {
                            ...item.connection,
                            unifiedType:
                                item.connection.Type === 'LakehouseDb' || item.connection.Type === 'LakehouseFiles'
                                    ? adfModelTypeMap.lakehouse.linkedServiceType
                                    : item.connection.Type
                        },
                        location: item.location
                    }))
                    .sort((conn1, conn2) => {
                        const result =
                            conn1.connection.unifiedType &&
                                conn2.connection.unifiedType &&
                                linkedServiceRegistry.has(conn1.connection.unifiedType) &&
                                linkedServiceRegistry.has(conn2.connection.unifiedType)
                                ? orderedLinkedServiceKind.indexOf(
                                    linkedServiceRegistry.getLinkedServiceConfiguration(conn1.connection.unifiedType)
                                        .linkedServiceKind
                                ) -
                                orderedLinkedServiceKind.indexOf(
                                    linkedServiceRegistry.getLinkedServiceConfiguration(conn2.connection.unifiedType)
                                        .linkedServiceKind
                                )
                                : 0;
                        if (
                            result === 0 &&
                            conn1.connection.unifiedType &&
                            conn2.connection.unifiedType &&
                            conn1.connection.unifiedType !== conn2.connection.unifiedType &&
                            [conn1.connection.unifiedType, conn2.connection.unifiedType].includes('Lakehouse')
                        ) {
                            return conn1.connection.unifiedType === 'Lakehouse' ? -1 : 1;
                        }
                        return result;
                    })
                    .map(async (item) => {
                        const modelService = await getServiceInstance<ModelService>(
                            this.context.contextData,
                            'ModelService'
                        );
                        const iframeService = await getServiceInstance<IFrameService>(
                            this.context.contextData,
                            'IFrameService'
                        );
                        const linkedServiceInfo = await findLinkedServiceFromContext(item.connection, {
                            modelService,
                            iframeService
                        });
                        if (linkedServiceInfo) {
                            const { dataset } = createDatasetWithLinkedService(
                                linkedServiceInfo.linkedService,
                                linkedServiceInfo.linkedServiceType,
                                model,
                                { linkedServiceRegistry, datasetRegistry },
                                {
                                    rootFolder: item.connection.Type === 'LakehouseFiles' ? 'Files' : 'Tables',
                                    isCopySink: item.location === 'destination'
                                }
                            );
                            if (dataset) {
                                dataset.customLinkedServiceType = linkedServiceInfo.linkedServiceType;

                                const datasetAdapter = this.context.asyncAdapterFor<
                                    Dataset,
                                    Model.Dataset,
                                    ActivityConnectionContext
                                >(Metamodel.modelOf(dataset));
                                if (item.location === 'source') {
                                    setSourceDataset(model, dataset, { linkedServiceRegistry });
                                    model.sourceDataset = await datasetAdapter.fromFabricCopilotContext(
                                        item.connection,
                                        model.sourceDataset
                                    );
                                    if (
                                        (item.connection as IDatabaseConnectionContext).Query &&
                                        model.source instanceof TabularCopySource
                                    ) {
                                        model.source.mode = 'query';
                                        model.source.readerQuery = (
                                            item.connection as IDatabaseConnectionContext
                                        ).Query;
                                    }
                                } else {
                                    setDestinationDataset(model, dataset);
                                    model.sinkDataset = await datasetAdapter.fromFabricCopilotContext(
                                        item.connection,
                                        model.sinkDataset
                                    );
                                }
                            }
                        }
                    })
            );
            await this._postUpdateActivity(model);
        }
        return model;
    }

    protected async _toResource(activity: CopyActivity): Promise<Model.CopyActivity> {
        const modelCopyActivity: Model.CopyActivity = super._getCommonActivityProperties(
            activity,
            this.model().name
        ) as Model.CopyActivity;
        modelCopyActivity.typeProperties = modelCopyActivity.typeProperties || { source: undefined, sink: undefined };

        let sourceDatasetSettings;
        let sinkDatasetSettings;
        if (activity.sourceDataset !== undefined) {
            if (this.context.adapterConfig.inlineDataset || this.context.adapterConfig.convertToFabric) {
                const sourceDatasetModel = Metamodel.modelOf(activity.sourceDataset);
                const sourceDatasetAdapter = this.context.asyncAdapterFor(sourceDatasetModel);
                if (sourceDatasetAdapter) {
                    sourceDatasetSettings = (await sourceDatasetAdapter.toResource(activity.sourceDataset)).properties;
                } else {
                    this._logError('Cannot find source dataset adapter in CopyActivityAdapter', {
                        datasetName: activity.sourceDataset.name,
                        datasetType: sourceDatasetModel?.name,
                    });
                }
            } else {
                modelCopyActivity.inputs = [this._getDatasetReference(activity, activity.sourceDataset, true)];
            }
        }
        if (activity.sinkDataset !== undefined) {
            if (this.context.adapterConfig.inlineDataset || this.context.adapterConfig.convertToFabric) {
                const sinkDatasetModel = Metamodel.modelOf(activity.sinkDataset);
                const sinkDatasetAdapter = this.context.asyncAdapterFor(sinkDatasetModel);
                if (sinkDatasetAdapter) {
                    sinkDatasetSettings = (await sinkDatasetAdapter.toResource(activity.sinkDataset)).properties;
                } else {
                    this._logError('Cannot find sink dataset adapter in CopyActivityAdapter', {
                        datasetName: activity.sinkDataset.name,
                        datasetType: sinkDatasetModel?.name,
                    });
                }
            } else {
                modelCopyActivity.outputs = [this._getDatasetReference(activity, activity.sinkDataset, false)];
            }
        }

        let linkedServiceType = '';
        if (activity.sourceDataset != null && activity.sourceDataset.linkedService != null) {
            linkedServiceType = Metamodel.modelOf(activity.sourceDataset.linkedService).name;
        }

        const sourceAdapter =
            activity.source != null ? this.context.asyncAdapterFor(Metamodel.modelOf(activity.source)) : undefined;
        const sinkAdapter =
            activity.copySink != null ? this.context.asyncAdapterFor(Metamodel.modelOf(activity.copySink)) : undefined;

        let stagingSettings;
        if (
            (activity.enableStaging || CopyActivity.ENABLE_STAGING.templateValue(activity)) &&
            activity.stagingSettings.storageLinkedService &&
            !activity.useFabricBuiltInStaging
        ) {
            stagingSettings = {
                linkedServiceName: linkedServiceReferenceToResource(
                    activity.stagingSettings.storageLinkedService,
                    this.context,
                    this.ensureLogger(),
                    activity.stagingSettings.linkedServiceParameters
                ),
                path: ValueAdapter.toResource(activity.stagingSettings, StagingSettings.PATH),
                enableCompression: ValueAdapter.toResource(activity.stagingSettings, StagingSettings.ENABLE_COMPRESSION)
            };
        }
        const enableSkipIncompatibleRow = ValueAdapter.toResource(
            activity.faultTolerenceSettings,
            FaultTolerenceSettings.ENABLE_SKIP_ROW
        );
        const redirectIncompatibleRowSettings =
            enableSkipIncompatibleRow && activity.faultTolerenceSettings.incompatibleRowLoggingLinkedService != null
                ? {
                    linkedServiceName: ReferenceAdapter.toResource(
                        activity.faultTolerenceSettings.incompatibleRowLoggingLinkedService,
                        this.context
                    ) as Model.LinkedServiceReference,
                    path: ValueAdapter.toResource(
                        activity.faultTolerenceSettings,
                        FaultTolerenceSettings.SKIP_ROW_STORAGE_PATH
                    )
                }
                : undefined;

        const datasetRegSvc = await getServiceInstance<DatasetRegistryService>(
            this.context.contextData,
            'DatasetRegistryService'
        );
        const linkedServiceRegSvc = await getServiceInstance<LinkedServiceRegistryService>(
            this.context.contextData,
            'LinkedServiceRegistryService'
        );
        const dataConsistencySupported = supportDataConsistency(activity, false, {
            linkedServiceRegSvc,
            datasetRegSvc
        });
        const skipMissingFileSupported = supportSkipMissingFile(
            activity.sourceDataset,
            activity.sinkDataset,
            activity.source
        );
        const skipErrorFile = {
            fileMissing:
                activity.faultTolerenceSettings.useNewFaultTolerance && skipMissingFileSupported
                    ? ValueAdapter.toResource(
                        activity.faultTolerenceSettings,
                        FaultTolerenceSettings.ENABLE_SKIP_MISSING_FILE
                    )
                    : undefined,
            fileForbidden:
                activity.faultTolerenceSettings.useNewFaultTolerance &&
                    supportSkipForbiddenFile(activity.sourceDataset, activity.sinkDataset, activity.source)
                    ? ValueAdapter.toResource(
                        activity.faultTolerenceSettings,
                        FaultTolerenceSettings.ENABLE_SKIP_FORBIDDEN_FILE
                    )
                    : undefined,
            invalidFileName:
                activity.faultTolerenceSettings.useNewFaultTolerance &&
                    supportSkipFilesWithInvalidNames(activity.sourceDataset, activity.sinkDataset, activity.source)
                    ? ValueAdapter.toResource(
                        activity.faultTolerenceSettings,
                        FaultTolerenceSettings.ENABLE_SKIP_FILES_WITH_INVALID_NAMES
                    )
                    : undefined,
            dataInconsistency:
                dataConsistencySupported &&
                    activity.sourceDataset instanceof BinaryDataset &&
                    activity.sinkDataset instanceof BinaryDataset &&
                    (activity.enableDataConsistency || CopyActivity.ENABLE_DATA_CONSISTENCY.templateValue(activity))
                    ? ValueAdapter.toResource(
                        activity.faultTolerenceSettings,
                        FaultTolerenceSettings.ENABLE_SKIP_INCONSISTENT_FILE
                    )
                    : undefined
        };

        const _supportPreserveRules = supportPreserveRules(activity.sourceDataset, activity.sinkDataset);

        modelCopyActivity.typeProperties = {
            source:
                sourceAdapter != null ? await sourceAdapter.toResource(activity.source, linkedServiceType) : undefined,
            sink: sinkAdapter != null ? await sinkAdapter.toResource(activity.copySink) : undefined,
            enableStaging: ValueAdapter.toResource(activity, CopyActivity.ENABLE_STAGING),
            stagingSettings,
            parallelCopies: ValueAdapter.toResource(activity, CopyActivity.PARALLEL_COPIES),
            adaptivePerformanceTuning: isAdaptivePerformanceTuningSupported(activity) ? ValueAdapter.toResource(activity, CopyActivity.ADAPTIVE_PERFORMANCE_TUNING) : undefined,
            preserve: _supportPreserveRules ? ValueAdapter.toResource(activity, CopyActivity.PRESERVE) : undefined,
            enableSkipIncompatibleRow,
            redirectIncompatibleRowSettings,
            skipErrorFile:
                skipErrorFile.fileMissing == null &&
                    skipErrorFile.dataInconsistency == null &&
                    skipErrorFile.fileForbidden == null &&
                    skipErrorFile.invalidFileName == null
                    ? undefined
                    : skipErrorFile,
            validateDataConsistency: dataConsistencySupported
                ? ValueAdapter.toResource(activity, CopyActivity.ENABLE_DATA_CONSISTENCY)
                : undefined
        };
        if (sourceDatasetSettings) {
            modelCopyActivity.typeProperties.source = modelCopyActivity.typeProperties.source || { type: undefined };
            modelCopyActivity.typeProperties.source.datasetSettings = sourceDatasetSettings;
        }
        if (sinkDatasetSettings) {
            modelCopyActivity.typeProperties.sink = modelCopyActivity.typeProperties.sink || { type: undefined };
            modelCopyActivity.typeProperties.sink.datasetSettings = sinkDatasetSettings;
        }

        if (activity.enableLogging || activity.enableOdbcDiagnosticLog) {
            if (activity.isNewLoggingModel && activity.loggingLocation.storageLinkedService) {
                const copyActivityLogSettings = {
                    logLevel: ValueAdapter.toResource(activity.loggingSettings, LoggingSettings.LOG_LEVEL),
                    enableReliableLogging: ValueAdapter.toResource(
                        activity.loggingSettings,
                        LoggingSettings.ENABLE_RELIABLE_LOGGING
                    )
                };
                modelCopyActivity.typeProperties.logSettings = {
                    enableCopyActivityLog: activity.enableLogging,
                    enableOdbcDiagnosticLog: activity.enableOdbcDiagnosticLog || undefined,
                    copyActivityLogSettings:
                        activity.enableLogging &&
                            (copyActivityLogSettings.logLevel != null ||
                                copyActivityLogSettings.enableReliableLogging != null)
                            ? copyActivityLogSettings
                            : undefined,
                    logLocationSettings: {
                        linkedServiceName: linkedServiceReferenceToResource(
                            activity.loggingLocation.storageLinkedService,
                            this.context,
                            this.ensureLogger(),
                            activity.loggingLocation.linkedServiceParameters
                        ),
                        path: ValueAdapter.toResource(activity.loggingLocation, LoggingLocation.PATH)
                    }
                };
            } else if (activity.enableLogging && activity.loggingLocation.storageLinkedService) {
                modelCopyActivity.typeProperties.logStorageSettings = {
                    linkedServiceName: linkedServiceReferenceToResource(
                        activity.loggingLocation.storageLinkedService,
                        this.context,
                        this.ensureLogger(),
                        activity.loggingLocation.linkedServiceParameters
                    ),
                    path: ValueAdapter.toResource(activity.loggingLocation, LoggingLocation.PATH)
                };
            }
        }

        modelCopyActivity.typeProperties.dataIntegrationUnits = ValueAdapter.toResource(
            activity,
            CopyActivity.CLOUD_DATAMOVEMENT_UNITS
        );

        const mapping = ValueAdapter.toResource(activity, CopyActivity.MAPPING);
        const sourceIsHierarchical =
            activity.sourceDataset != null &&
            datasetRegSvc.getDatasetConfiguration(Metamodel.modelOf(activity.sourceDataset).name).datasetKind ===
            'hierarchical';
        const sinkIsHierarchical =
            activity.sinkDataset != null &&
            datasetRegSvc.getDatasetConfiguration(Metamodel.modelOf(activity.sinkDataset).name).datasetKind ===
            'hierarchical';
        const collectionReference = sourceIsHierarchical
            ? ValueAdapter.toResource(activity, CopyActivity.COLLECTION_REFERENCE)
            : undefined;
        const mapComplexValuesToString =
            sourceIsHierarchical && !sinkIsHierarchical ? activity.mapComplexValuesToString : undefined;
        const sourceDatasetConfig =
            activity.sourceDataset != null
                ? datasetRegSvc.getDatasetConfiguration(Metamodel.modelOf(activity.sourceDataset).name)
                : undefined;
        const sinkDatasetConfig =
            activity.sinkDataset != null
                ? datasetRegSvc.getDatasetConfiguration(Metamodel.modelOf(activity.sinkDataset).name)
                : undefined;
        const isDeltaClone = isDeltaCloneCopy(activity);
        const typeConversionSupported = !isDeltaClone && supportTypeConversion(activity, false, datasetRegSvc);

        if (
            activity.sourceDataset?.isV2 || activity.sinkDataset?.isV2
        ) {
            if (
                !(
                    (activity.sourceDataset && activity.sourceDataset instanceof BinaryDataset) ||
                    (activity.sinkDataset && activity.sinkDataset instanceof BinaryDataset) ||
                    isDeltaClone
                )
            ) {
                // new v2 mapping
                if (CopyActivity.MAPPING_V2.templateValue(activity)) {
                    modelCopyActivity.typeProperties.translator = ValueAdapter.toResource(
                        activity,
                        CopyActivity.MAPPING_V2
                    );
                } else if (activity.mappingV2?.length > 0) {
                    const keepSinkPhysicalType = supportEditPhysicalType(sinkDatasetConfig, activity.sinkDataset);
                    const isSourceSql = supportSqlEditSinkSchemaPhysicalType(sourceDatasetConfig);
                    const isSinkSql = supportSqlEditSinkSchemaPhysicalType(sinkDatasetConfig);
                    const sourceShowLogicType =
                        sourceDatasetConfig &&
                        (resultIsTrue(sourceDatasetConfig.notSupportPhysicalType, activity.sourceDataset) ||
                            (__IS_FABRIC_DPE__ && sourceDatasetConfig.datasetKind === 'hierarchical'));
                    const useParquetSinkType = activity.enableAdvancedParquetSinkType && (activity.sinkDataset instanceof ParquetDataset);
                    modelCopyActivity.typeProperties.translator = {
                        type: 'TabularTranslator',
                        mappings: activity.mappingV2.map((item, index) => {
                            return {
                                source: toMappingItem(
                                    activity,
                                    keepSinkPhysicalType,
                                    typeConversionSupported,
                                    sourceShowLogicType,
                                    isSourceSql,
                                    isSinkSql,
                                    item.source,
                                    true
                                ),
                                sink: toMappingItem(
                                    activity,
                                    keepSinkPhysicalType,
                                    typeConversionSupported,
                                    sourceShowLogicType,
                                    isSourceSql,
                                    isSinkSql,
                                    item.sink,
                                    false,
                                    index,
                                    useParquetSinkType
                                )
                            };
                        }),
                        collectionReference,
                        mapComplexValuesToString
                    };
                } else if (mapComplexValuesToString != null) {
                    modelCopyActivity.typeProperties.translator = {
                        type: 'TabularTranslator',
                        // mapComplexValuesToString doesn't rely on activity.mappingV2 value, it can be standalone settings in TabularTranslator
                        mapComplexValuesToString
                    };
                }
            }
        } else if (mapping != null && (!sourceIsHierarchical || !sinkIsHierarchical)) {
            // don't generate mapping when source and sink is Hierarchical data
            if (mapping.type !== 'Expression') {
                if (isMap(mapping) && mapping.size > 0) {
                    if (sourceIsHierarchical || sinkIsHierarchical) {
                        // schema mapping
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const mapObj: any = {};
                        for (const [key, val] of mapping) {
                            mapObj[val] = key;
                        }
                        modelCopyActivity.typeProperties.translator = {
                            type: 'TabularTranslator',
                            schemaMapping: mapObj,
                            collectionReference
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        } as any;
                    } else {
                        // column mapping
                        if (await this._irEnableStructuralMapping(activity)) {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const mapObj: any = {};
                            for (const [key, val] of mapping) {
                                mapObj[val] = key;
                            }
                            modelCopyActivity.typeProperties.translator = {
                                type: 'TabularTranslator',
                                columnMappings: mapObj
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            } as any;
                        } else {
                            const mappingStrings: string[] = [];
                            mapping.forEach((val, key) => mappingStrings.push(val + ': ' + key));
                            modelCopyActivity.typeProperties.translator = {
                                type: 'TabularTranslator',
                                columnMappings: mappingStrings.reduce(
                                    (accumulator, current) => accumulator + ', ' + current
                                )
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            } as any;
                        }

                        // special case handling for azure table as sink
                        const reverseMapping = new Map<string, string>();
                        mapping.forEach((val, key) => reverseMapping.set(val, key));
                        if (reverseMapping.size > 0 && activity.copySink instanceof AzureTableSink) {
                            const sink = modelCopyActivity.typeProperties.sink as Model.AzureTableSink;
                            if (
                                sink.azureTablePartitionKeyName &&
                                sink.azureTablePartitionKeyName.type !== 'Expression' &&
                                reverseMapping.has(sink.azureTablePartitionKeyName)
                            ) {
                                sink.azureTablePartitionKeyName = reverseMapping.get(sink.azureTablePartitionKeyName);
                            }
                            if (
                                sink.azureTableRowKeyName &&
                                sink.azureTableRowKeyName.type !== 'Expression' &&
                                reverseMapping.has(sink.azureTableRowKeyName)
                            ) {
                                sink.azureTableRowKeyName = reverseMapping.get(sink.azureTableRowKeyName);
                            }
                        }
                    }
                } else if (collectionReference != null && collectionReference !== '') {
                    modelCopyActivity.typeProperties.translator = {
                        type: 'TabularTranslator',
                        collectionReference
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    } as any;
                }
            } else {
                modelCopyActivity.typeProperties.translator = mapping;
            }
        }

        // Generate type conversion
        if (typeConversionSupported && !CopyActivity.MAPPING_V2.templateValue(activity)) {
            const translator = modelCopyActivity.typeProperties.translator || { type: 'TabularTranslator' };
            translator.typeConversion = activity.enableTypeConversion || undefined;
            if (translator.typeConversion) {
                translator.typeConversionSettings = {};
                translator.typeConversionSettings.allowDataTruncation = ValueAdapter.toResource(
                    activity.typeConversionSettings,
                    TypeConversionSettings.ALLOW_DATA_TRUNCATION
                );
                translator.typeConversionSettings.treatBooleanAsNumber = ValueAdapter.toResource(
                    activity.typeConversionSettings,
                    TypeConversionSettings.TREAT_BOOLEAN_AS_NUMBER
                );
                translator.typeConversionSettings.dateFormat =
                    ValueAdapter.toResource(activity.typeConversionSettings, TypeConversionSettings.DATE_FORMAT) ||
                    undefined;
                translator.typeConversionSettings.dateTimeFormat =
                    ValueAdapter.toResource(activity.typeConversionSettings, TypeConversionSettings.DATETIME_FORMAT) ||
                    undefined;
                translator.typeConversionSettings.dateTimeOffsetFormat =
                    ValueAdapter.toResource(
                        activity.typeConversionSettings,
                        TypeConversionSettings.DATETIME_OFFSET_FORMAT
                    ) || undefined;
                translator.typeConversionSettings.timeSpanFormat =
                    ValueAdapter.toResource(activity.typeConversionSettings, TypeConversionSettings.TIME_SPAN_FORMAT) ||
                    undefined;
                translator.typeConversionSettings.timeFormat =
                    ValueAdapter.toResource(activity.typeConversionSettings, TypeConversionSettings.TIME_FORMAT) ||
                    undefined;
                translator.typeConversionSettings.culture =
                    ValueAdapter.toResource(activity.typeConversionSettings, TypeConversionSettings.CULTURE) ||
                    undefined;
            }
            modelCopyActivity.typeProperties.translator =
                Object.keys(translator).filter((key) => translator[key]).length > 1 ? translator : undefined;
        }

        // Set columnFlattenSettings when:
        // source is hierarchical and not using schema mapping
        // either source or sink supports flatten columns
        if (
            __IS_FABRIC_DPE__ &&
            !isDeltaClone &&
            ((sourceIsHierarchical && !sinkIsHierarchical) || sourceDatasetConfig?.sourceSupportFlattenColumns) &&
            activity.enableTypeConversion
        ) {
            const translator = modelCopyActivity.typeProperties.translator || { type: 'TabularTranslator' };
            const translatorIsExpression =
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (translator as any).type === 'Expression' ||
                (typeof translator === 'string' && isLogicAppExpression(translator));
            if (!translatorIsExpression) {
                translator.columnFlattenSettings = {
                    ...(await ModelUtilties.toResource(this.context, activity.columnFlattenSettings))
                };
                modelCopyActivity.typeProperties.translator =
                    Object.keys(translator).filter((key) => translator[key]).length > 1 ? translator : undefined;
            }
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            merge(modelCopyActivity, parsed);
        }

        return modelCopyActivity;
    }

    protected async _toModel(modelActivity: Model.CopyActivity): Promise<CopyActivity> {
        const rawObject = {};

        const activity: CopyActivity = this.model().create<CopyActivity>(modelActivity.name);
        this._updatedCommonProperties(modelActivity, activity);
        modelActivity.typeProperties = modelActivity.typeProperties || { source: undefined, sink: undefined };

        if (modelActivity.inputs && modelActivity.inputs.length > 0) {
            activity.sourceDataset = ReferenceAdapter.toProxyModel<Dataset>(
                Metamodel.modelFor(Dataset),
                modelActivity.inputs[0],
                this.context
            );
            if (modelActivity.inputs[0].parameters) {
                convertParametersToModel(
                    activity,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    activity.sourceDataset,
                    activity.sourceDatasetParameters,
                    modelActivity.inputs[0].parameters
                );
            }
        }

        if (modelActivity.outputs && modelActivity.outputs.length > 0) {
            activity.sinkDataset = ReferenceAdapter.toProxyModel<Dataset>(
                Metamodel.modelFor(Dataset),
                modelActivity.outputs[0],
                this.context
            );
            if (modelActivity.outputs[0].parameters) {
                convertParametersToModel(
                    activity,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    activity.sinkDataset,
                    activity.sinkDatasetParameters,
                    modelActivity.outputs[0].parameters
                );
            }
        }

        if (modelActivity.typeProperties.source?.type) {
            if (modelActivity.typeProperties.source.type !== 'Expression') {
                let copySourceModel: IModel | undefined;
                try {
                    copySourceModel = Metamodel.modelByName(modelActivity.typeProperties.source.type);
                } catch {
                    copySourceModel = this._registerCopySource(modelActivity.typeProperties.source.type);
                }
                const sourceAdapter = this.context.asyncAdapterFor(copySourceModel);

                if (sourceAdapter != null) {
                    activity.source = await sourceAdapter.toModel(modelActivity.typeProperties.source, activity);

                    if (!(activity.source instanceof CopySource)) {
                        throw new Error(
                            `Dataset source ${modelActivity.typeProperties.source.type} for ${activity.name} is not a valid dataset source`
                        );
                    }
                    merge(rawObject, {
                        typeProperties: {
                            source: {
                                ...(await ValueAdapter.remainder(
                                    this.context,
                                    modelActivity.typeProperties.source,
                                    copySourceModel
                                )),
                                datasetSettings: undefined
                            }
                        }
                    });
                }

                const sourceDatasetSettings = modelActivity.typeProperties.source.datasetSettings;
                if (sourceDatasetSettings?.type) {
                    const sourceDatasetModel = Metamodel.modelByName(sourceDatasetSettings.type);
                    if (sourceDatasetModel) {
                        const sourceDataset = Owned.create<Dataset>(sourceDatasetModel, activity);
                        const sourceDatasetAdapter = this.context.asyncAdapterFor(sourceDatasetModel);
                        if (sourceDatasetAdapter) {
                            await sourceDatasetAdapter.toModel(
                                { name: generateUUID().replace(/-/g, '_'), properties: sourceDatasetSettings },
                                sourceDataset
                            );
                        }
                        activity.sourceDataset = sourceDataset;

                        if (this._shouldGetArtifactDMTSConnectionFromSolutionTemplate()) {
                            const dmtsCredentialKey = `${modelActivity.name}/Copy/source`;
                            const dmtsCredentialForArtifact = this.context.adapterConfig.artifactDMTSCredentials.get(dmtsCredentialKey);
                            if (dmtsCredentialForArtifact != null) {
                                activity.sourceDataset.dmtsCredentialForArtifact = dmtsCredentialForArtifact;
                            }
                        }
                    }
                }
            } else {
                merge(rawObject, {
                    typeProperties: {
                        source: modelActivity.typeProperties.source
                    }
                });
            }
        }

        // special case handling for azure table as sink
        if (activity.mapping && activity.mapping.size > 0 && modelActivity.typeProperties.sink != null) {
            const sink = modelActivity.typeProperties.sink as Model.AzureTableSink;
            if (
                sink.azureTablePartitionKeyName &&
                sink.azureTablePartitionKeyName.type !== 'Expression' &&
                activity.mapping.has(sink.azureTablePartitionKeyName)
            ) {
                sink.azureTablePartitionKeyName = activity.mapping.get(sink.azureTablePartitionKeyName);
            }
            if (
                sink.azureTableRowKeyName &&
                sink.azureTableRowKeyName.type !== 'Expression' &&
                activity.mapping.has(sink.azureTableRowKeyName)
            ) {
                sink.azureTableRowKeyName = activity.mapping.get(sink.azureTableRowKeyName);
            }
        }

        if (modelActivity.typeProperties.sink?.type) {
            if (modelActivity.typeProperties.sink.type !== 'Expression') {
                let copySinkModel: IModel | undefined;
                try {
                    copySinkModel = Metamodel.modelByName(modelActivity.typeProperties.sink.type);
                } catch {
                    copySinkModel = this._registerCopySink(modelActivity.typeProperties.sink.type);
                }
                const sinkAdapter = this.context.asyncAdapterFor(copySinkModel);

                if (sinkAdapter != null) {
                    activity.copySink = await sinkAdapter.toModel(modelActivity.typeProperties.sink, activity);

                    merge(rawObject, {
                        typeProperties: {
                            sink: {
                                ...(await ValueAdapter.remainder(
                                    this.context,
                                    modelActivity.typeProperties.sink,
                                    copySinkModel
                                )),
                                datasetSettings: undefined
                            }
                        }
                    });
                }

                const sinkDatasetSettings = modelActivity.typeProperties.sink.datasetSettings;
                if (sinkDatasetSettings?.type) {
                    const sinkDatasetModel = Metamodel.modelByName(sinkDatasetSettings.type);
                    if (sinkDatasetModel) {
                        const sinkDataset = Owned.create<Dataset>(sinkDatasetModel, activity);
                        const sinkDatasetAdapter = this.context.asyncAdapterFor(sinkDatasetModel);
                        if (sinkDatasetAdapter) {
                            await sinkDatasetAdapter.toModel(
                                { name: generateUUID().replace(/-/g, '_'), properties: sinkDatasetSettings },
                                sinkDataset
                            );
                        }
                        activity.sinkDataset = sinkDataset;

                        if (this._shouldGetArtifactDMTSConnectionFromSolutionTemplate()) {
                            const dmtsCredentialKey = `${modelActivity.name}/Copy/destination`;
                            const dmtsCredentialForArtifact = this.context.adapterConfig.artifactDMTSCredentials.get(dmtsCredentialKey);
                            if (dmtsCredentialForArtifact != null) {
                                activity.sinkDataset.dmtsCredentialForArtifact = dmtsCredentialForArtifact;
                            }
                        }
                    }
                    activity.originalData.sinkLinkedServiceType = sinkDatasetSettings.linkedService?.properties?.type;
                }
            } else {
                merge(rawObject, {
                    typeProperties: {
                        sink: modelActivity.typeProperties.sink
                    }
                });
            }
        }

        const translator = modelActivity.typeProperties.translator;
        activity.enableTypeConversion = undefined;
        if (translator != null) {
            if (
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (translator as any).type === 'Expression' ||
                (typeof translator === 'string' && isLogicAppExpression(translator))
            ) {
                ValueAdapter.toModel(activity, CopyActivity.MAPPING, translator);
                // put this expression to both v1 and v2 mapping in UI model, because here we can't distinguish v1 or v2
                ValueAdapter.toModel(activity, CopyActivity.MAPPING_V2, translator);
                if (activity.mappingV2 == null) {
                    activity.mappingV2 = [];
                }
            } else {
                try {
                    if (translator.typeConversion != null) {
                        activity.enableTypeConversion = translator.typeConversion;
                        if (translator.typeConversion && translator.typeConversionSettings) {
                            ValueAdapter.toModel(
                                activity.typeConversionSettings,
                                TypeConversionSettings.ALLOW_DATA_TRUNCATION,
                                translator.typeConversionSettings.allowDataTruncation
                            );
                            ValueAdapter.toModel(
                                activity.typeConversionSettings,
                                TypeConversionSettings.TREAT_BOOLEAN_AS_NUMBER,
                                translator.typeConversionSettings.treatBooleanAsNumber
                            );
                            ValueAdapter.toModel(
                                activity.typeConversionSettings,
                                TypeConversionSettings.DATE_FORMAT,
                                translator.typeConversionSettings.dateFormat
                            );
                            ValueAdapter.toModel(
                                activity.typeConversionSettings,
                                TypeConversionSettings.DATETIME_FORMAT,
                                translator.typeConversionSettings.dateTimeFormat
                            );
                            ValueAdapter.toModel(
                                activity.typeConversionSettings,
                                TypeConversionSettings.DATETIME_OFFSET_FORMAT,
                                translator.typeConversionSettings.dateTimeOffsetFormat
                            );
                            ValueAdapter.toModel(
                                activity.typeConversionSettings,
                                TypeConversionSettings.TIME_SPAN_FORMAT,
                                translator.typeConversionSettings.timeSpanFormat
                            );
                            ValueAdapter.toModel(
                                activity.typeConversionSettings,
                                TypeConversionSettings.TIME_FORMAT,
                                translator.typeConversionSettings.timeFormat
                            );
                            ValueAdapter.toModel(
                                activity.typeConversionSettings,
                                TypeConversionSettings.CULTURE,
                                translator.typeConversionSettings.culture
                            );
                        }
                    }

                    if (__IS_FABRIC_DPE__ && translator.columnFlattenSettings) {
                        ValueAdapter.toModel(
                            activity.columnFlattenSettings,
                            ColumnFlattenSettings.TREAT_ARRAY_AS_STRING,
                            translator.columnFlattenSettings.treatArrayAsString
                        );
                        ValueAdapter.toModel(
                            activity.columnFlattenSettings,
                            ColumnFlattenSettings.TREAT_STRUCT_AS_STRING,
                            translator.columnFlattenSettings.treatStructAsString
                        );
                        ValueAdapter.toModel(
                            activity.columnFlattenSettings,
                            ColumnFlattenSettings.FLATTEN_COLUMN_DELIMITER,
                            translator.columnFlattenSettings.flattenColumnDelimiter
                        );
                    }

                    if ((translator as Model.TabularTranslator).mappings != null) {
                        // handle v2 mapping
                        const mapping = translator as Model.TabularTranslator;
                        activity.mappingV2 = [];
                        const toMappingItem = (col: Model.MappingItem): Column => {
                            const result = new Column();
                            if (col != null) {
                                result.name = col.name;
                                result.columnType = col.type;
                                result.physicalType = col.physicalType;
                                result.format = col.format;
                                result.index = col.ordinal;
                                result.path = col.path;
                                result.culture = col.culture;
                                result.scale = col.scale;
                                result.precision = col.precision;
                                result.size = col.length;
                                result.unit = col.unit;
                                result.isNullable = col.isNullable;
                                result.parquetPorperties = col.parquetProperties as IParquetPorperties;
                            }
                            return result;
                        };
                        activity.hasExistingMapping = true;
                        activity.enableAdvancedParquetSinkType = mapping.mappings.find((pair) => pair.sink?.parquetProperties != null) != null;
                        mapping.mappings.forEach((pair) => {
                            const newPair = new ColumnMappingPair();
                            newPair.source = toMappingItem(pair.source);
                            newPair.sink = toMappingItem(pair.sink);
                            activity.mappingV2.push(newPair);
                            // update cache columns
                            const source = toMappingItem(pair.source);
                            const sink = toMappingItem(pair.sink);
                            activity.cacheSourceSchema.push(source);
                            activity.cacheSinkSchema.push(sink);
                        });
                    } else {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const mapping = (translator as any).schemaMapping || (translator as any).columnMappings;
                        if (typeof mapping === 'string') {
                            const subMappings = (mapping as string).split(',');
                            subMappings.forEach((substr) => {
                                const valkey = substr.split(':');
                                if (valkey.length < 2) {
                                    throw new Error();
                                }
                                activity.mapping.set(valkey[1].trim(), valkey[0].trim());
                            });
                        } else {
                            for (const property in mapping) {
                                activity.mapping.set(mapping[property], property);
                            }
                        }
                        activity.mapping.forEach((v, k) => {
                            const source = Column.MODEL.create<Column>();
                            source.name = v;
                            const sink = Column.MODEL.create<Column>();
                            sink.name = k;
                            activity.cacheSourceSchema.push(source);
                            activity.cacheSinkSchema.push(sink);
                        });
                    }
                } catch (error) {
                    // To be safe, we only log activity name instead of any message that may contain column name (user metadata)
                    this._logError('Invalid mapping in copy activity: ' + activity.name);
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                activity.collectionReference = (translator as any).collectionReference;
                activity.mapComplexValuesToString = translator.mapComplexValuesToString;
            }
        }

        ValueAdapter.toModel(activity, CopyActivity.ENABLE_STAGING, modelActivity.typeProperties.enableStaging);
        if (modelActivity.typeProperties.enableStaging) {
            if (modelActivity.typeProperties.stagingSettings) {
                activity.useFabricBuiltInStaging = false;
                const stagingSettings = modelActivity.typeProperties.stagingSettings;
                activity.stagingSettings.storageLinkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                    Metamodel.modelFor(LinkedService),
                    stagingSettings.linkedServiceName,
                    this.context
                );

                activity.originalData.stagingLinkedServiceType = Metamodel.modelOf(
                    activity.stagingSettings.storageLinkedService
                )?.name;

                if (stagingSettings.linkedServiceName && stagingSettings.linkedServiceName.parameters) {
                    convertParametersToModel(
                        activity.stagingSettings,
                        !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                        activity.stagingSettings.storageLinkedService,
                        activity.stagingSettings.linkedServiceParameters,
                        stagingSettings.linkedServiceName.parameters
                    );
                }
                ValueAdapter.toModel(
                    activity.stagingSettings,
                    StagingSettings.PATH,
                    modelActivity.typeProperties.stagingSettings.path
                );
                ValueAdapter.toModel(
                    activity.stagingSettings,
                    StagingSettings.ENABLE_COMPRESSION,
                    modelActivity.typeProperties.stagingSettings.enableCompression
                );
                activity.stagingSettings.enableCompression = stagingSettings.enableCompression;
            }
        }

        if (modelActivity.typeProperties.logSettings) {
            activity.isNewLoggingModel = true;
            const loggingSettings = modelActivity.typeProperties.logSettings;
            activity.enableLogging = loggingSettings.enableCopyActivityLog;
            activity.enableOdbcDiagnosticLog = loggingSettings.enableOdbcDiagnosticLog;
            if (loggingSettings.logLocationSettings) {
                const logLocationSettings = loggingSettings.logLocationSettings;
                activity.loggingLocation.storageLinkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                    Metamodel.modelFor(LinkedService),
                    logLocationSettings.linkedServiceName,
                    this.context
                );
                if (logLocationSettings.linkedServiceName && logLocationSettings.linkedServiceName.parameters) {
                    convertParametersToModel(
                        activity.loggingLocation,
                        !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                        activity.loggingLocation.storageLinkedService,
                        activity.loggingLocation.linkedServiceParameters,
                        logLocationSettings.linkedServiceName.parameters
                    );
                }
                ValueAdapter.toModel(activity.loggingLocation, LoggingLocation.PATH, logLocationSettings.path);
            }

            if (modelActivity.typeProperties.logSettings.copyActivityLogSettings) {
                const copyActivityLogSettings = modelActivity.typeProperties.logSettings.copyActivityLogSettings;
                ValueAdapter.toModel(
                    activity.loggingSettings,
                    LoggingSettings.LOG_LEVEL,
                    copyActivityLogSettings.logLevel
                );
                ValueAdapter.toModel(
                    activity.loggingSettings,
                    LoggingSettings.ENABLE_RELIABLE_LOGGING,
                    copyActivityLogSettings.enableReliableLogging
                );
            }
        } else if (modelActivity.typeProperties.logStorageSettings) {
            const logStorageSettings = modelActivity.typeProperties.logStorageSettings;
            activity.loggingLocation.storageLinkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                Metamodel.modelFor(LinkedService),
                logStorageSettings.linkedServiceName,
                this.context
            );
            if (logStorageSettings.linkedServiceName && logStorageSettings.linkedServiceName.parameters) {
                convertParametersToModel(
                    activity.loggingLocation,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    activity.loggingLocation.storageLinkedService,
                    activity.loggingLocation.linkedServiceParameters,
                    logStorageSettings.linkedServiceName.parameters
                );
            }
            ValueAdapter.toModel(activity.loggingLocation, LoggingLocation.PATH, logStorageSettings.path);
            activity.enableLogging = true;
            activity.isNewLoggingModel = false;
        }

        ValueAdapter.toModel(
            activity.faultTolerenceSettings,
            FaultTolerenceSettings.ENABLE_SKIP_ROW,
            modelActivity.typeProperties.enableSkipIncompatibleRow
        );
        if (
            modelActivity.typeProperties.enableSkipIncompatibleRow &&
            modelActivity.typeProperties.redirectIncompatibleRowSettings != null
        ) {
            activity.faultTolerenceSettings.incompatibleRowLoggingLinkedService =
                ReferenceAdapter.toProxyModel<LinkedService>(
                    Metamodel.modelFor(LinkedService),
                    modelActivity.typeProperties.redirectIncompatibleRowSettings.linkedServiceName,
                    this.context
                );
            ValueAdapter.toModel(
                activity.faultTolerenceSettings,
                FaultTolerenceSettings.SKIP_ROW_STORAGE_PATH,
                modelActivity.typeProperties.redirectIncompatibleRowSettings.path
            );
            activity.faultTolerenceSettings.useNewFaultTolerance = false;
        }

        ValueAdapter.toModel(
            activity,
            CopyActivity.ENABLE_DATA_CONSISTENCY,
            modelActivity.typeProperties.validateDataConsistency
        );
        if (modelActivity.typeProperties.skipErrorFile) {
            ValueAdapter.toModel(
                activity.faultTolerenceSettings,
                FaultTolerenceSettings.ENABLE_SKIP_MISSING_FILE,
                modelActivity.typeProperties.skipErrorFile.fileMissing
            );
            ValueAdapter.toModel(
                activity.faultTolerenceSettings,
                FaultTolerenceSettings.ENABLE_SKIP_FORBIDDEN_FILE,
                modelActivity.typeProperties.skipErrorFile.fileForbidden
            );
            ValueAdapter.toModel(
                activity.faultTolerenceSettings,
                FaultTolerenceSettings.ENABLE_SKIP_INCONSISTENT_FILE,
                modelActivity.typeProperties.skipErrorFile.dataInconsistency
            );
            ValueAdapter.toModel(
                activity.faultTolerenceSettings,
                FaultTolerenceSettings.ENABLE_SKIP_FILES_WITH_INVALID_NAMES,
                modelActivity.typeProperties.skipErrorFile.invalidFileName
            );
        }

        ValueAdapter.toModel(activity, CopyActivity.PARALLEL_COPIES, modelActivity.typeProperties.parallelCopies);
        if (__IS_FABRIC_DPE__) {
            ValueAdapter.toModel(activity, CopyActivity.ADAPTIVE_PERFORMANCE_TUNING, modelActivity.typeProperties.adaptivePerformanceTuning);
        }
        if (modelActivity.typeProperties.dataIntegrationUnits != null) {
            ValueAdapter.toModel(
                activity,
                CopyActivity.CLOUD_DATAMOVEMENT_UNITS,
                modelActivity.typeProperties.dataIntegrationUnits
            );
        } else if (modelActivity.typeProperties.cloudDataMovementUnits != null) {
            ValueAdapter.toModel(
                activity,
                CopyActivity.CLOUD_DATAMOVEMENT_UNITS,
                modelActivity.typeProperties.cloudDataMovementUnits
            );
        }

        // handle case issue and backward compatibility
        let preserveRules =
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            modelActivity.typeProperties.preserve || (modelActivity.typeProperties as any).preserveRules;
        if (preserveRules != null && Array.isArray(preserveRules)) {
            preserveRules = preserveRules.map((r: string) => {
                const value = r.toLowerCase();
                switch (value) {
                    case preserveRulesValues.acl.toLowerCase():
                        return preserveRulesValues.acl;
                    case preserveRulesValues.group.toLowerCase():
                        return preserveRulesValues.group;
                    case preserveRulesValues.owner.toLowerCase():
                        return preserveRulesValues.owner;
                    default:
                        return r;
                }
            });
        }
        ValueAdapter.toModel(activity, CopyActivity.PRESERVE, preserveRules);

        merge(rawObject, await ValueAdapter.remainder(this.context, modelActivity, this.model()));
        activity.raw = JSON.stringify(rawObject);

        return activity;
    }

    private _getDatasetReference(activity: CopyActivity, dataset: Dataset, isSource: boolean): Model.DatasetReference {
        const datasetReference = ReferenceAdapter.toResource(dataset, this.context) as Model.DatasetReference;
        const datasetParameters = isSource ? activity.sourceDatasetParameters : activity.sinkDatasetParameters;
        datasetReference.parameters = fillParameterValuesForResource(
            datasetParameters,
            this.context,
            this.ensureLogger()
        );
        return datasetReference;
    }

    private _registerCopySource(copySourceType: string): IModel {
        const model = Metamodel.createModel(CopySource.MODEL, {
            name: copySourceType,
            display: [copySourceType],
            createdFromUnknownType: true
        });
        AdapterRegistry.createAdapter(
            {
                model,
                asynchronous: true,
                adapts: {
                    type: '*',
                    sourceRetryCount: '*',
                    sourceRetryWait: '*'
                }
            },
            GenericCopySourceAdapter
        );
        return model;
    }

    private _registerCopySink(copySinkType: string): IModel {
        const model = Metamodel.createModel(CopySink.MODEL, {
            name: copySinkType,
            display: [copySinkType],
            createdFromUnknownType: true
        });
        AdapterRegistry.createAdapter(
            {
                model,
                asynchronous: true,
                adapts: {
                    type: '*'
                }
            },
            GenericCopySinkAdapter
        );
        return model;
    }

    private async _irEnableStructuralMapping(activity: CopyActivity): Promise<boolean> {
        if (GlobalVariables.isCmdApiApp) {
            return true;
        }
        // TODO: We could potentially just always return true since this code was checking a specific SHIR version of 3 years ago.
        const sourceIR =
            activity.sourceDataset && activity.sourceDataset.linkedService
                ? activity.sourceDataset.linkedService.integrationRuntime
                : undefined;
        const sinkIR =
            activity.sinkDataset && activity.sinkDataset.linkedService
                ? activity.sinkDataset.linkedService.integrationRuntime
                : undefined;
        const irStatusService = await getServiceInstance<IntegrationRuntimeStatusService>(
            this.context.contextData,
            'IntegrationRuntimeStatusService'
        );
        if (sourceIR && sourceIR instanceof SelfHostedIntegrationRuntime) {
            const sourceIRStatus = await irStatusService
                .getAndRefreshIRStatus(sourceIR.name)
                .pipe(filter((result) => result.status !== 'Getting'))
                .pipe(take(1))
                .toPromise();
            if (sourceIRStatus && sourceIRStatus.status === 'Succeeded' && sourceIRStatus.result) {
                const irTypeProp = sourceIRStatus.result as Model.SelfHostedIntegrationRuntimeStatus;
                if (irTypeProp && irTypeProp.typeProperties && irTypeProp.typeProperties.version) {
                    sourceIR.version = irTypeProp.typeProperties.version;
                }
            }
        }

        if (sinkIR && sinkIR instanceof SelfHostedIntegrationRuntime) {
            const sinkIRStatus = await irStatusService
                .getAndRefreshIRStatus(sinkIR.name)
                .pipe(filter((result) => result.status !== 'Getting'))
                .pipe(take(1))
                .toPromise();
            if (sinkIRStatus && sinkIRStatus.status === 'Succeeded' && sinkIRStatus.result) {
                const irTypeProp = sinkIRStatus.result as Model.SelfHostedIntegrationRuntimeStatus;
                if (irTypeProp && irTypeProp.typeProperties && irTypeProp.typeProperties.version) {
                    sinkIR.version = irTypeProp.typeProperties.version;
                }
            }
        }

        if (
            (sourceIR === undefined || sourceIR instanceof ManagedIntegrationRuntime) &&
            (sinkIR === undefined || sinkIR instanceof ManagedIntegrationRuntime)
        ) {
            // both cloud IR
            return true;
        } else if (sourceIR instanceof SelfHostedIntegrationRuntime && sourceIR.version) {
            // assume sourceIR and sinkIR must be same when they are both self-hosted IR
            return true;
        } else if (sinkIR instanceof SelfHostedIntegrationRuntime && sinkIR.version) {
            return true;
        } else {
            return false;
        }
    }

    private async _postUpdateActivity(model: CopyActivity): Promise<void> {
        // post-updating
        // 1. handle staging
        const modelService = await getServiceInstance<ModelService>(this.context.contextData, 'ModelService');
        await CopyActivity.validateEnableStaging(model, {
            contextData: this.context.contextData,
            ...modelService.modelAttributeContext
        })
            .then((violations) => {
                if (violations?.length > 0) {
                    model.enableStaging = true;
                }
            })
            .catch((e) => noop);
        // 2. handle auto table creation
        if (model.copySink && model.sinkDataset) {
            const datasetRegistry = await getServiceInstance<DatasetRegistryService>(
                this.context.contextData,
                'DatasetRegistryService'
            );
            const copySinkModel = Metamodel.modelOf(model.copySink);
            const sinkMetadata = datasetRegistry.getDatasetConfiguration(Metamodel.modelOf(model.sinkDataset).name);
            const autoTableCreation = supportAutoTableCreation(sinkMetadata, model.sinkDataset);
            if (autoTableCreation && copySinkModel.hasAttribute('tableOption')) {
                ValueAdapter.toModel(
                    model.copySink,
                    copySinkModel.attribute('tableOption'),
                    tableOptionValues.autoCreate
                );
            }
        }
        // 3. handle db
        setDbFromLs(model.sourceDataset, model.sourceDataset?.linkedService);
        setDbFromLs(model.sinkDataset, model.sinkDataset?.linkedService);
    }

    private _getLSTpeFromDatasetMetadataAndConnectionKind(datasetRegistry: DatasetRegistryService, iframeService: IFrameService, dataset: Dataset): string {
        return getLinkedServiceTypeFromDatasetMetadataAndConnectionKind(
            dataset,
            dataset.linkedService?.tridentConnectionKind,
            {
                iframeService,
                datasetRegistry
            }
        );
    }
}

/**
 * Use this array to sort connections before dataset creation
 * Useful when we want to fine-tune source & destination dataset type to ensure a compatible source & destination pair
 * Format of file-type dataset is determined by the other end so with the lower priority
 */
const orderedLinkedServiceKind = ['table', 'hierarchical', 'file', 'unknown'];
